import { EntityDetectionRequest, EntityDetectionResponse } from "../types";

export class EntityDetectionService {
  private debounceTimer: NodeJS.Timeout | null = null;
  private pendingRequest: Promise<EntityDetectionResponse | null> | null = null;
  private latestRequest: EntityDetectionRequest | null = null;
  private readonly debounceDelay: number;

  constructor(debounceDelay: number = 500) {
    this.debounceDelay = debounceDelay;
  }

  async detectEntities(
    url: string,
    request: EntityDetectionRequest,
    requestParams?: RequestInit
  ): Promise<EntityDetectionResponse | null> {
    return new Promise(resolve => {
      // Store the latest request
      this.latestRequest = request;

      // Cancel previous timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(async () => {
        // If there's a pending request, wait for it to complete first
        if (this.pendingRequest) {
          await this.pendingRequest;
        }

        // Check if this is still the latest request
        if (this.latestRequest === request) {
          try {
            // Create and store the new request promise
            this.pendingRequest = this.makeRequest(url, request, requestParams);
            const result = await this.pendingRequest;

            // Clear pending request after completion
            this.pendingRequest = null;
            resolve(result);
          } catch (error) {
            console.error("Entity detection error:", error);
            this.pendingRequest = null;
            resolve(null);
          }
        } else {
          // This request is stale, resolve with null
          resolve(null);
        }
      }, this.debounceDelay);
    });
  }

  private async makeRequest(
    url: string,
    request: EntityDetectionRequest,
    requestParams?: RequestInit
  ): Promise<EntityDetectionResponse | null> {
    const response = await fetch(url, {
      ...requestParams,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...requestParams?.headers,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Entity detection failed: ${response.statusText}`);
    }

    return await response.json();
  }

  cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    // Reset pending request reference (we can't abort a promise)
    this.pendingRequest = null;
    this.latestRequest = null;
  }
}
